import { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Lock, CheckCircle, User, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const PreRegister = () => {
  const [clientType, setClientType] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formatPhone = (value) => {
    const numbers = value.replace(/\\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleSendCode = async () => {
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setSending(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/pre-register/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      if (response.ok) {
        setCodeSent(true);
        toast.success(data.dev_code ? `Code sent! TEST: ${data.dev_code}` : 'Code sent to your email');
      } else {
        toast.error(data.detail || 'Failed to send code');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Please enter 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/pre-register/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const data = await response.json();
      if (response.ok && data.verified) {
        setEmailVerified(true);
        toast.success('Email verified!');
      } else {
        toast.error(data.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!clientType || !firstName || !lastName || !phone || !email || !emailVerified || !consent) {
      toast.error('Please complete all required fields');
      return;
    }
    if (clientType === 'business' && !businessName) {
      toast.error('Please enter your business name');
      return;
    }

    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const phoneDigits = phone.replace(/\\D/g, '');
      const response = await fetch(`${backendUrl}/api/v1/pre-register/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_type: clientType,
          first_name: firstName,
          last_name: lastName,
          phone: `+1${phoneDigits}`,
          email,
          business_name: clientType === 'business' ? businessName : null
        })
      });

      const data = await response.json();
      if (response.ok) {
        setSubmitted(true);
        toast.success('Pre-registration complete!');
      } else {
        toast.error(data.detail || 'Submission failed');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240]">
        <Helmet><title>Pre-Registration Complete | KGOB</title>
        <meta name="description" content="Your pre-registration with KGOB CPAs & Advisors is complete." />
        <meta name="robots" content="noindex, follow" /></Helmet>
        <Header />
        <main className="py-20">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-['Playfair_Display'] text-4xl font-bold text-white mb-4">You're on the List!</h1>
              <p className="text-lg text-white/90 mb-8">Thank you for pre-registering for the KGOB Client Portal.</p>
              <Card className="p-8 text-left mb-6">
                <h2 className="font-semibold text-[#0B2240] mb-4">What Happens Next:</h2>
                <div className="space-y-3 text-sm text-[#4C5A70]">
                  <p>1. <strong>Portal Launch:</strong> January 2026</p>
                  <p>2. <strong>You'll receive:</strong> Invitation email with login instructions</p>
                  <p>3. <strong>Portal Features:</strong> Upload documents, view deliverables, track work status</p>
                </div>
              </Card>
              <Button data-testid="pre-register-button-4" asChild className="h-12 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="pre-register-link-1" to="/">Return to Home</Link>
              </Button>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240]">
      <Helmet><title>Pre-Register for Client Portal | KGOB</title></Helmet>
      <Header />
      
      <main className="py-12 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <div className="flex justify-center gap-4 mb-6">
              <Lock className="w-12 h-12 text-[#D4AF37]" />
              <Lock className="w-12 h-12 text-[#D4AF37]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-white mb-6">
              Pre-Register for Your 2026 Client Portal
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              We're rolling out an updated client portal to make it easier to upload documents, view deliverables, and track the status of your work in one place. You can pre-register now so we can get your profile ready before tax season kicks off.
            </p>
          </motion.div>

          <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
            <div className="space-y-6">
              <div>
                <Label className="text-[#0B2240] font-semibold text-base mb-3 block">Are you registering as:</Label>
                <RadioGroup value={clientType} onValueChange={setClientType}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div data-testid="pre-register-div-2" className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer ${
                      clientType === 'individual' ? 'border-[#D4AF37] bg-[#F7F6F2]' : 'border-[#E1DFD7]'
                    }`} onClick={() => setClientType('individual')}>
                      <RadioGroupItem value="individual" id="individual" />
                      <Label htmlFor="individual" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-5 h-5 text-[#D4AF37]" />
                        <span>Individual/Family</span>
                      </Label>
                    </div>
                    <div data-testid="pre-register-div-1" className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer ${
                      clientType === 'business' ? 'border-[#D4AF37] bg-[#F7F6F2]' : 'border-[#E1DFD7]'
                    }`} onClick={() => setClientType('business')}>
                      <RadioGroupItem value="business" id="business" />
                      <Label htmlFor="business" className="flex items-center gap-2 cursor-pointer">
                        <Building className="w-5 h-5 text-[#D4AF37]" />
                        <span>Business Owner</span>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#0B2240] font-semibold text-sm">First Name *</Label>
                  <Input data-testid="pre-register-input-6" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" className="mt-2" />
                </div>
                <div>
                  <Label className="text-[#0B2240] font-semibold text-sm">Last Name *</Label>
                  <Input data-testid="pre-register-input-5" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" className="mt-2" />
                </div>
              </div>

              <div>
                <Label className="text-[#0B2240] font-semibold text-sm">Phone Number *</Label>
                <Input data-testid="pre-register-input-4" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" className="mt-2" />
              </div>

              {clientType === 'business' && (
                <div>
                  <Label className="text-[#0B2240] font-semibold text-sm">Business Name *</Label>
                  <Input data-testid="pre-register-input-3" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Your Company LLC" className="mt-2" />
                </div>
              )}

              <div>
                <Label className="text-[#0B2240] font-semibold text-sm">Email *</Label>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="pre-register-input-2" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" disabled={codeSent} className="flex-1" />
                  {!emailVerified && !codeSent && (
                    <Button data-testid="pre-register-button-3" onClick={handleSendCode} disabled={sending} className="bg-[#D4AF37] text-white">{sending ? 'Sending...' : 'Send Code'}</Button>
                  )}
                  {emailVerified && <CheckCircle className="w-6 h-6 text-green-600 mt-2" />}
                </div>
              </div>

              {codeSent && !emailVerified && (
                <div>
                  <Label className="text-[#0B2240] font-semibold text-sm">Verification Code</Label>
                  <div className="flex gap-2 mt-2">
                    <Input data-testid="pre-register-input-1" value={code} onChange={(e) => setCode(e.target.value.replace(/\\D/g, '').slice(0, 6))} placeholder="123456" className="text-center text-xl" maxLength={6} />
                    <Button data-testid="pre-register-button-2" onClick={handleVerify} disabled={verifying || code.length !== 6} className="bg-[#D4AF37] text-white">{verifying ? 'Verifying...' : 'Verify'}</Button>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3 bg-[#F7F6F2] p-4 rounded-lg">
                <Checkbox data-testid="pre-register-checkbox-1" checked={consent} onCheckedChange={setConsent} id="consent" />
                <Label htmlFor="consent" className="text-sm cursor-pointer">
                  I understand the KGOB Client Portal will launch in <strong>January 2026</strong>, and I'll receive an invitation email when it's ready.
                </Label>
              </div>

              <Button data-testid="pre-register-button-1"
                onClick={handleSubmit}
                disabled={!clientType || !firstName || !lastName || !phone || !emailVerified || !consent || submitting || (clientType === 'business' && !businessName)}
                className="w-full h-14 text-lg bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
              >
                {submitting ? 'Submitting...' : 'Pre-Register My Portal'}
              </Button>
            </div>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PreRegister;
